import {
  createStoreSlice as createSlice,
  type PayloadAction,
} from '@/store/core';
import { RootState } from '@/store';
import { StoreError } from '@/types/storeError.interface';
import type { MissingItemFormSummaryDTO } from '@/api/missing-item-form/DTOs';

interface MissingItemFormState {
  /** True while the open form's own foreground fetch is in flight. Drives the MissingItemsPage skeleton. */
  loading: boolean;
  /** True while the sidebar's form list is in flight. Drives the dashboard's missing-items gate. */
  userFormsLoading: boolean;
  error: StoreError | null;
  userMissingItemForms: MissingItemFormSummaryDTO[];
  currentMissingItemFormId: number | null;
}

const initialState: MissingItemFormState = {
  loading: false,
  userFormsLoading: false,
  error: null,
  userMissingItemForms: [],
  currentMissingItemFormId: null,
};

export const missingItemFormSlice = createSlice({
  name: 'missingItemForm',
  initialState,
  reducers: {
    missingItemFormLoading: state => {
      state.loading = true;
    },
    userMissingItemFormsLoading: state => {
      state.userFormsLoading = true;
    },
    // Leaves `loading` alone: the sidebar's form list and the form currently open are
    // different resources, and clearing it here would drop a skeleton the open form's
    // own fetch is still waiting on.
    fetchUserMissingItemFormsSuccess: (
      state,
      action: PayloadAction<MissingItemFormSummaryDTO[]>,
    ) => {
      state.userFormsLoading = false;
      state.error = null;
      state.userMissingItemForms = action.payload;
    },
    fetchUserMissingItemFormsFailure: (
      state,
      action: PayloadAction<StoreError>,
    ) => {
      state.userFormsLoading = false;
      state.error = action.payload;
    },
    fetchMissingItemFormSuccess: (
      state,
      action: PayloadAction<{ id: number; background?: boolean }>,
    ) => {
      state.error = null;

      // A background refresh (the reload-required fetch after a save) must not clear a
      // skeleton some other foreground fetch on this page is still waiting on, and a
      // late one for a form the user already navigated away from must not become the
      // "current" one.
      if (!action.payload.background) {
        state.loading = false;
        state.currentMissingItemFormId = action.payload.id;
      }
    },
    fetchMissingItemFormFailure: (state, action: PayloadAction<StoreError>) => {
      state.loading = false;
      state.error = action.payload;
    },
    clearUserMissingItemForms: state => {
      state.userMissingItemForms = [];
      state.currentMissingItemFormId = null;
    },
  },
});

export const {
  missingItemFormLoading,
  userMissingItemFormsLoading,
  fetchUserMissingItemFormsSuccess,
  fetchUserMissingItemFormsFailure,
  fetchMissingItemFormSuccess,
  fetchMissingItemFormFailure,
  clearUserMissingItemForms,
} = missingItemFormSlice.actions;

export const selectMissingItemForm = (state: RootState) =>
  state[missingItemFormSlice.name] as typeof initialState;
